import {clearWorkspaceMemory} from './workspaceMemory';
import CustomerAccessRoute from './components/CustomerAccessRoute';
import BillingPage from './components/BillingPage';
import BillingPolicyPage from './components/BillingPolicyPage';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import './App.css';
import './AuthPage.css';
import { ShieldCheck, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Dashboard from './components/Dashboard';
import Alerts from './components/Alerts';
import IncidentsPage from './components/IncidentsPage';
import ResponseCenter from './components/ResponseCenter';
import Reports from './components/Reports';
import Settings from './components/Setting';
import Support from './components/Support';
import ResetPasswordPage from './components/ResetPasswordPage';
import Cookies from 'js-cookie';
import ProfileSettings from './components/ProfileSetting';
import DevicesPage from './components/DevicesPage';
import GatewayFleetPage from './components/GatewayFleetPage';
import IngestionsPage from './components/IngestionsPage';
import SyslogPage from './components/SyslogPage';
import OrganizationAdminPage from './components/OrganizationAdminPage';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import { jwtDecode } from 'jwt-decode';
import { OrganizationProvider, ACTIVE_ORGANIZATION_KEY } from './context/OrganizationContext';
import CustomerSessionMonitor, { validateCustomerSession, signOutCustomerSession } from './CustomerSession';


const AUTH_API_BASE=(process.env.REACT_APP_CYBEDEFENDER_AI_URL||'/api').replace(/\/$/,'');

function AuthPages({ isAuthenticated, setIsAuthenticated }) {
  const [activeTab, setActiveTab] = useState('Login');
  const [submitting,setSubmitting]=useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordAgain, setShowPasswordAgain] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  const [resetLinkError, setResetLinkError] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  
  const resetFormData = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setError('');
    setForgotPassword(false);
    resetFormData();
    setRegistrationSuccess(false);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      setError('Please fill out all fields.');
      return;
    }
    const loginData = {
      email: formData.email,
      password: formData.password,
    };

    try {
      const response = await fetch(`${AUTH_API_BASE}/cybedefender/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Unable to complete this request. Please retry.');
      }
      Cookies.set('token', data.token);
      Cookies.set('guid', data.guid);
      Cookies.set('email',data.email);
      Cookies.set('username',data.name);
      localStorage.setItem('token', data.token);
      setIsAuthenticated(true);
    } catch (error) {
      setError(error.message || 'Login failed. Please try again.');
      console.error('Error:', error);
    }
  };

  const handleForgotPasswordClick = () => {
    setForgotPassword(true);
    setError('');
    resetFormData();
  };

  const handleSendResetLink = async () => {
    if (!formData.email) {
      setError('Please enter your email.');
      return;
    }
  
    try {
      const response = await fetch(`${AUTH_API_BASE}/cybedefender/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to send reset link.');
      }
  
      const data = await response.json();
      console.log('Reset link sent:', data);
      setResetLinkSent(true);
      setResetLinkError(false);
    } catch (error) {
      setResetLinkError(true);
      setResetLinkSent(false);
      setError('');
      console.error('Error:', error);
    }
  };

   // const handleResetPassword = async () => {
  //   if (!formData.password || !formData.confirmPassword) {
  //     setError('Please fill out all fields.');
  //     return;
  //   }
  //   if (formData.password !== formData.confirmPassword) {
  //     setError('Passwords do not match.');
  //     return;
  //   }
  //   if (!validatePassword(formData.password)) {
  //     setError('Password must be at least 8 characters long, include 1 uppercase letter, 1 number, and 1 special character.');
  //     return;
  //   }
  
  //   try {
  //     const response = await fetch(`${AUTH_API_BASE}/cybedefender/reset-password`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({
  //         email: formData.email, // Email from the reset link
  //         newPassword: formData.password,
  //       }),
  //     });
  
  //     if (!response.ok) {
  //       throw new Error('Failed to reset password.');
  //     }
  
  //     const data = await response.json();
  //     console.log('Password reset successful:', data);
  //     // setResetPasswordPage(false); // Go back to login page
  //     setError(''); // Clear any errors
  //     handleTabClick('Login'); // Redirect to login page
  //   } catch (error) {
  //     setError('Failed to reset password. Please try again.');
  //     console.error('Error:', error);
  //   }
  // };

  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password) && new TextEncoder().encode(password).length <= 72;
  };

  const handleRegister = async () => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill out all fields.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!validatePassword(formData.password)) {
      setError('Password must be at least 8 characters long, include 1 uppercase letter, 1 number, and 1 special character.');
      return;
    }

    if (!termsAccepted) {
      setError('Please accept the terms & conditions.');
      return;
    }

    const user_guid = uuidv4();

    const registrationData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      user_guid: user_guid
    };

    try {
      const response = await fetch(`${AUTH_API_BASE}/cybedefender/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Unable to complete this request. Please retry.');
      }
      console.log('Registration successful:', data);
      setRegistrationSuccess(true);
    } catch (error) {
      setError(error.message || 'Registration failed. Please try again.');
      console.error('Error:', error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const togglePasswordVisibilityAgain = () => {
    setShowPasswordAgain(!showPasswordAgain);
  };

  const openTermsModal = (e) => {
    e.preventDefault();
    setIsTermsModalOpen(true);
  };

  const closeTermsModal = () => {
    setIsTermsModalOpen(false);
  };
  return (
    <main className="auth-page">
      <section className="auth-card" aria-labelledby="auth-title">
        <a href="/" className="auth-brand" aria-label="CybeDefender home"><span className="auth-brand-mark"><ShieldCheck size={23}/></span><span>CybeDefender<span className="auth-brand-ai"> AI</span></span></a>
        {!registrationSuccess && !forgotPassword && <div className="auth-tabs" aria-label="Account access">
          <button type="button" aria-pressed={activeTab==='Login'} disabled={submitting} className={activeTab==='Login'?'active':''} onClick={()=>handleTabClick('Login')}>Sign in</button>
          <button type="button" aria-pressed={activeTab==='Register'} disabled={submitting} className={activeTab==='Register'?'active':''} onClick={()=>handleTabClick('Register')}>Create account</button>
        </div>}
        <div className="auth-heading">
          <h1 id="auth-title">{registrationSuccess?'Account created':forgotPassword?(resetLinkSent?'Check your email':resetLinkError?'Try again':'Reset your password'):activeTab==='Login'?'Welcome back':'Create your account'}</h1>
          <p>{registrationSuccess?'Your account is ready. Sign in to continue.':forgotPassword?(resetLinkSent?'If an account matches that email, a reset link will arrive shortly.':resetLinkError?'We could not complete your request. Please try again.':'Enter your email to receive a reset link.'):activeTab==='Login'?'Sign in to your security workspace.':'Get started with CybeDefender.'}</p>
        </div>
        {error && <div className="auth-error" role="alert">{error}</div>}
        {registrationSuccess || (forgotPassword && (resetLinkSent||resetLinkError)) ? <div className="auth-result">
          {registrationSuccess || resetLinkSent ? <CheckCircle2 size={36} aria-hidden="true"/>:null}
          {resetLinkError && <button type="button" className="auth-primary" onClick={()=>{setResetLinkError(false);setError('');}}>Try again</button>}
          <button type="button" className={resetLinkError?'auth-back':'auth-primary'} onClick={()=>handleTabClick('Login')}>Back to sign in</button>
        </div> : <form className="auth-form" onSubmit={async e=>{e.preventDefault();if(submitting)return;setSubmitting(true);setError('');try{await (forgotPassword?handleSendResetLink():activeTab==='Login'?handleLogin():handleRegister());}finally{setSubmitting(false);}}}>
          {!forgotPassword && activeTab==='Register' && <div className="auth-field"><label htmlFor="auth-name">Full name</label><input id="auth-name" type="text" name="name" autoComplete="name" placeholder="Your name" value={formData.name} onChange={handleInputChange} disabled={submitting}/></div>}
          <div className="auth-field"><label htmlFor="auth-email">Email</label><input id="auth-email" type="email" name="email" autoComplete="email" placeholder="you@company.com" value={formData.email} onChange={handleInputChange} disabled={submitting}/></div>
          {!forgotPassword && <>
            <div className="auth-field"><div className="auth-label-row"><label htmlFor="auth-password">Password</label>{activeTab==='Login'&&<button type="button" className="auth-link" disabled={submitting} onClick={handleForgotPasswordClick}>Forgot password?</button>}</div>
              <div className="auth-password"><input id="auth-password" type={showPassword?'text':'password'} name="password" autoComplete={activeTab==='Login'?'current-password':'new-password'} placeholder="Enter your password" value={formData.password} onChange={handleInputChange} disabled={submitting}/>
                <button type="button" className="auth-eye" aria-label={showPassword?'Hide password':'Show password'} aria-pressed={showPassword} onClick={togglePasswordVisibility}>{showPassword?<EyeOff size={18}/>:<Eye size={18}/>}</button>
              </div>
            </div>
            {activeTab==='Register'&&<>
              <p className="auth-password-hint">8+ characters with an uppercase letter, a number and a symbol (@$!%*?&).</p>
              <div className="auth-field"><label htmlFor="auth-confirm">Confirm password</label><div className="auth-password"><input id="auth-confirm" type={showPasswordAgain?'text':'password'} name="confirmPassword" autoComplete="new-password" placeholder="Re-enter your password" value={formData.confirmPassword} onChange={handleInputChange} disabled={submitting}/><button type="button" className="auth-eye" aria-label={showPasswordAgain?'Hide confirmation password':'Show confirmation password'} aria-pressed={showPasswordAgain} onClick={togglePasswordVisibilityAgain}>{showPasswordAgain?<EyeOff size={18}/>:<Eye size={18}/>}</button></div></div>
              <div className="auth-terms"><input type="checkbox" id="terms" checked={termsAccepted} disabled={submitting} onChange={()=>setTermsAccepted(!termsAccepted)}/><label htmlFor="terms">I agree to the <a href="/terms" onClick={openTermsModal}>Terms of use &amp; Privacy Policy</a>.</label></div>
            </>}
          </>}
          <button className="auth-primary" type="submit" disabled={submitting}>{submitting?(forgotPassword?'Sending…':activeTab==='Login'?'Signing in…':'Creating account…'):(forgotPassword?'Send reset link':activeTab==='Login'?'Sign in':'Create account')}</button>
          {forgotPassword&&<button type="button" className="auth-back" disabled={submitting} onClick={()=>handleTabClick('Login')}><ArrowLeft size={15}/>Back to sign in</button>}
        </form>}
      </section>
      {isTermsModalOpen && <TermsAndConditionsModal isOpen={isTermsModalOpen} onClose={closeTermsModal}/>}
    </main>
  );
}

const clearCustomerAuthState = () => {
  clearWorkspaceMemory();
  localStorage.removeItem('token');
  localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  ['token', 'guid', 'email', 'username'].forEach((name) => Cookies.remove(name));
};

function CustomerApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true); 

  const [sessionError,setSessionError]=useState('');
  const [logoutError,setLogoutError]=useState(''),[signingOut,setSigningOut]=useState(false);const logoutLock=useRef(false);
  useEffect(() => {
    const controller=new AbortController();
    async function checkSavedSession() {
      const token=localStorage.getItem('token');
      if(!token){clearCustomerAuthState();setIsAuthenticated(false);setLoading(false);return;}
      try {
        const decoded=jwtDecode(token);
        if(typeof decoded.exp!=='number' || decoded.exp<Date.now()/1000 || (decoded.purpose && decoded.purpose!=='access')) {
          clearCustomerAuthState();setIsAuthenticated(false);setLoading(false);return;
        }
      } catch(error) {clearCustomerAuthState();setIsAuthenticated(false);setLoading(false);return;}
      try {
        const valid=await validateCustomerSession(token,controller.signal);
        if(controller.signal.aborted || token!==localStorage.getItem('token'))return;
        if(!valid)clearCustomerAuthState();
        setIsAuthenticated(valid);
      } catch(error) {
        if(!controller.signal.aborted)setSessionError('Unable to verify your session. Check your connection and retry.');
      } finally {if(!controller.signal.aborted)setLoading(false);}
    }
    checkSavedSession();
    return ()=>controller.abort();
  }, []);

  const invalidateCustomerSession = useCallback(() => {
    clearCustomerAuthState();setSessionError('');setLogoutError('');setIsAuthenticated(false);setLoading(false);
  }, []);
  const handleLogout = useCallback(async () => {
    if(logoutLock.current)return false;logoutLock.current=true;setSigningOut(true);setLogoutError('');const token=localStorage.getItem('token');
    try {await signOutCustomerSession(token);if(token!==localStorage.getItem('token'))return false;invalidateCustomerSession();return true;}
    catch(error){setLogoutError(error.message);return false;}
    finally{logoutLock.current=false;setSigningOut(false);}
  }, [invalidateCustomerSession]);

  if (loading) return <div role="status" style={{padding:32}}>Verifying your session…</div>;
  if (sessionError) return <div role="alert" style={{padding:32}}><p>{logoutError||sessionError}</p><button onClick={()=>window.location.reload()}>Retry</button> <button onClick={handleLogout}>Sign out</button></div>; 

  return (
    <Router>
      {logoutError&&<div role="alert" className="fixed right-6 top-20 z-50 max-w-sm rounded-lg border border-red-200 bg-white p-4 text-sm text-red-700 shadow-lg">{logoutError}<button type="button" className="ml-3 font-semibold underline" onClick={handleLogout} disabled={signingOut}>Retry sign out</button></div>}
      <OrganizationProvider isAuthenticated={isAuthenticated} onAuthenticationFailure={invalidateCustomerSession}>
        <CustomerSessionMonitor isAuthenticated={isAuthenticated} onInvalidated={invalidateCustomerSession}/>
        <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" />
            ) : (
              <AuthPages
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
              />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><Dashboard onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/alerts"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><Alerts onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/incidents"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><IncidentsPage onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/responses"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><ResponseCenter onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route path="/billing" element={isAuthenticated ? <CustomerAccessRoute><BillingPage onLogout={handleLogout}/></CustomerAccessRoute> : <Navigate to="/"/>}/>
        <Route
          path="/reports"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><Reports onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/settings"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><Settings onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/gateways"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><GatewayFleetPage onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/devices"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><DevicesPage onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/ingestions"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><IngestionsPage onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/syslog"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><SyslogPage onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/support"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><Support onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/admin/*"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><OrganizationAdminPage onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
         <Route
          path="/profile-settings"
          element={
            isAuthenticated ? (
              <CustomerAccessRoute><ProfileSettings onLogout={handleLogout} /></CustomerAccessRoute>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        
        <Route
          path="/reset-password/:token"
          element={<ResetPasswordPage />}
        />
        </Routes>
      </OrganizationProvider>
    </Router>
  );
}

function App(){
  if(window.location.pathname.replace(/\/+$/,'')==='/billing-policy')return <BillingPolicyPage/>;
  return <CustomerApp/>;
}
export default App;